import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';

import { CompactingMessage } from './components/compacting-message';
import { KimMessage } from './components/kim-message';
import { ReasoningTempWindow } from './components/reasoning-temp-window';
import { ReferenceSidePanel } from './components/reference-side-panel';
import {
  ContentBlock,
  KimMessageType,
  ReferenceType,
  StreamBlock,
  StreamKimMessageType,
} from '../../../../../../../../../../backend/src/types/types';
import { KimInput } from '../components/kim-input';
import { KimStreamChunk, useKimStream } from '@/api/hooks/useKimStream';
import { useSendKimMessage } from '@/api/mutations/useSendKimMessage';
import { useGetKimMessages } from '@/api/queries/useGetKimMessages';
import { Icon } from '@/new/components/icon';

const SHOW_DEBUG_INFO = false;

export function NewKimThread() {
  const { matterId, subsetId, threadId } = useParams();

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const {
    data: messages,
    isLoading: messagesLoading,
    isSuccess: messagesLoaded,
  } = useGetKimMessages(matterId, subsetId, threadId);

  const initialLastMessageId = useMemo(() => {
    if (!messagesLoaded || !messages?.length) return undefined;

    const lastMessage = messages[messages.length - 1];
    return String(new Date(lastMessage.createdDate).getTime());
  }, [messages, messagesLoaded]);

  const { chunks, content, isConnected } = useKimStream({
    matterId: matterId!,
    subsetId: subsetId!,
    threadId: threadId!,
    enabled: Boolean(matterId && subsetId && threadId && messagesLoaded),
    initialLastMessageId,
  });

  useEffect(() => {
    if (scrollContainerRef.current && chunks.length > 0) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [content, chunks.length]);

  const [sidePanel, setSidePanel] = useState<{
    isOpen: boolean;
    activeId: string | null;
    activeType: ReferenceType | null;
  }>({ isOpen: false, activeId: null, activeType: null });

  const openSidePanel = useCallback((id: string, referenceType: ReferenceType) => {
    setSidePanel({ isOpen: true, activeId: id, activeType: referenceType });
  }, []);

  const closeSidePanel = useCallback(() => {
    setSidePanel({ isOpen: false, activeId: null, activeType: null });
  }, []);

  // Reset scroll and close side panel when switching threads
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
    closeSidePanel();
  }, [threadId]);

  const [inputValue, setInputValue] = useState('');
  const { mutate: sendMessage, isLoading: isSending } = useSendKimMessage(matterId, subsetId, threadId);

  const isContentBlock = (block: StreamBlock): block is ContentBlock => block.type !== 'end';

  // Same as below - likely will need to rework
  const referenceIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    let index = 1;
    const trackBlock = (block: ContentBlock) => {
      if (block.type === 'reference' && !map.has(block.id)) {
        map.set(block.id, index++);
      }
    };
    messages?.forEach((msg) => msg.content.forEach(trackBlock));
    chunks.forEach((chunk) => {
      if (isContentBlock(chunk.content)) trackBlock(chunk.content);
    });
    return map;
  }, [messages, chunks]);

  const collectedReferences = useMemo(() => {
    const refs: { id: string; index: number; referenceType: ReferenceType }[] = [];
    const seen = new Set<string>();
    const trackBlock = (block: ContentBlock) => {
      if (block.type === 'reference' && block.refType === ReferenceType.Fact && !seen.has(block.id)) {
        seen.add(block.id);
        refs.push({ id: block.id, index: referenceIndexMap.get(block.id) ?? 0, referenceType: block.refType });
      }
    };
    messages?.forEach((msg) => msg.content.forEach(trackBlock));
    chunks.forEach((chunk) => {
      if (isContentBlock(chunk.content)) trackBlock(chunk.content);
    });
    return refs;
  }, [messages, chunks, referenceIndexMap]);

  // Group consecutive reasoning-temp chunks into separate windows.
  // A new window starts whenever a non-reasoning-temp chunk appears between reasoning-temp chunks.
  const tempReasoningGroups = useMemo(() => {
    const groups: { chunks: KimStreamChunk[]; items: string[]; timestamp: number }[] = [];
    let sawNonTemp = false;

    for (const chunk of chunks) {
      if (chunk.type === StreamKimMessageType.AiReasoningTemp) {
        const lastGroup = groups[groups.length - 1];
        if (!lastGroup || sawNonTemp) {
          // Start a new group
          groups.push({ chunks: [chunk], items: [], timestamp: chunk.timestamp });
          sawNonTemp = false;
        } else {
          lastGroup.chunks.push(chunk);
        }
        // Extract text items
        if (chunk.content.type === 'text') {
          groups[groups.length - 1].items.push((chunk.content as { type: 'text'; value: string }).value);
        }
      } else {
        sawNonTemp = true;
      }
    }

    return groups;
  }, [chunks]);

  // This currently groups streamed chunks every time a new chunk comes through - I don't think this
  // will work long-term. I think we'll need a different approach with even types like message
  // start/add to message/message end to more efficiently group chunks.
  const groupedChunks = useMemo(() => {
    const groups: { type: KimMessageType; content: ContentBlock[]; isComplete: boolean; timestamp: number }[] = [];
    for (const chunk of chunks) {
      // Skip temp reasoning and compacting chunks — they're rendered separately
      if (chunk.type === StreamKimMessageType.AiReasoningTemp) continue;
      if (chunk.type === KimMessageType.Compacting) continue;

      if (chunk.content.type === 'start') continue;

      if (chunk.content.type === 'end') {
        const last = groups[groups.length - 1];
        if (last) last.isComplete = true;
        continue;
      }

      const last = groups[groups.length - 1];

      if (last && last.type === chunk.type && chunk.type !== KimMessageType.AiReasoning) {
        last.content.push(chunk.content);
      } else {
        groups.push({
          type: chunk.type as KimMessageType,
          content: [chunk.content],
          isComplete: false,
          timestamp: chunk.timestamp,
        });
      }
    }
    return groups;
  }, [chunks]);

  // Track compacting events from the stream
  const compactingEvents = useMemo(() => {
    const events: { isComplete: boolean; timestamp: number }[] = [];
    for (const chunk of chunks) {
      if (chunk.type !== KimMessageType.Compacting) continue;
      if (chunk.content.type === 'start') {
        events.push({ isComplete: false, timestamp: chunk.timestamp });
      } else if (chunk.content.type === 'end') {
        const last = events[events.length - 1];
        if (last) last.isComplete = true;
      }
    }
    return events;
  }, [chunks]);

  // Build a unified render list so each reasoning temp window stays at its timestamp position
  const streamItems = useMemo(() => {
    const items: (
      | { kind: 'message'; type: KimMessageType; content: ContentBlock[]; isComplete: boolean; timestamp: number }
      | { kind: 'reasoning-temp'; groupIndex: number; items: string[]; totalCount: number; timestamp: number }
      | { kind: 'compacting'; isComplete: boolean; timestamp: number }
    )[] = [];

    for (const group of groupedChunks) {
      items.push({ kind: 'message', ...group });
    }

    tempReasoningGroups.forEach((group, idx) => {
      items.push({
        kind: 'reasoning-temp',
        groupIndex: idx,
        items: group.items,
        totalCount: group.chunks.length,
        timestamp: group.timestamp,
      });
    });

    for (const event of compactingEvents) {
      items.push({ kind: 'compacting', ...event });
    }

    items.sort((a, b) => a.timestamp - b.timestamp);
    return items;
  }, [groupedChunks, tempReasoningGroups, compactingEvents]);

  // Show spinner when the last message is from the user and no stream content has appeared after it
  const isWaitingForResponse = useMemo(() => {
    if (!messagesLoaded || !messages?.length) return false;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg.type !== KimMessageType.User) return false;
    const lastMsgTime = new Date(lastMsg.createdDate).getTime();
    return !streamItems.some((item) => item.timestamp > lastMsgTime);
  }, [messagesLoaded, messages, streamItems]);

  const handleSendMessage = () => {
    const text = inputValue.trim();
    if (!text || isSending) return;

    setInputValue('');
    sendMessage(text);
  };

  return (
    <div className="flex h-full w-full">
      {/* Message column */}
      <div className="mr-2 flex min-w-0 grow flex-col pb-0">
        <div className="flex w-full grow flex-col justify-between overflow-hidden">
          <div ref={scrollContainerRef} className="min-h-0 grow space-y-4 overflow-y-auto font-serif">
            {messagesLoading && <div className="my-8 text-center text-sm text-gray-400">Loading messages...</div>}

            <div className="mx-auto max-w-3xl px-4 py-24">
              {messages?.map((msg) =>
                msg.type === KimMessageType.Compacting ? (
                  <CompactingMessage key={`db-${msg.id}`} isComplete />
                ) : (
                  <KimMessage
                    key={`db-${msg.id}`}
                    type={msg.type}
                    content={msg.content}
                    isComplete
                    referenceIndexMap={referenceIndexMap}
                    debugLabel={`db | id: ${msg.id} | created: ${msg.createdDate}`}
                    onReferenceClick={openSidePanel}
                  />
                ),
              )}

              {streamItems.map((item, i) =>
                item.kind === 'reasoning-temp' ? (
                  <ReasoningTempWindow
                    key={`reasoning-temp-${item.groupIndex}`}
                    items={item.items}
                    totalCount={item.totalCount}
                  />
                ) : item.kind === 'compacting' ? (
                  <CompactingMessage key={`compacting-${i}`} isComplete={item.isComplete} />
                ) : (
                  <KimMessage
                    key={`stream-${i}`}
                    type={item.type}
                    content={item.content}
                    isComplete={item.isComplete}
                    referenceIndexMap={referenceIndexMap}
                    debugLabel={`stream | group ${i} | created: ${new Date(item.timestamp).toISOString()}`}
                    onReferenceClick={openSidePanel}
                  />
                ),
              )}

              {isWaitingForResponse && (
                <div className="my-4">
                  <Icon className="lucide--loader ml-3 animate-spin text-base text-gray-400" />
                </div>
              )}
            </div>
          </div>

          <div className="-m-1 mx-auto mb-1 w-full max-w-3xl space-y-2 p-1">
            {SHOW_DEBUG_INFO && (
              <div className="flex items-center justify-end gap-2">
                <span className={`text-sm ${isConnected ? 'text-green-600' : 'text-red-500'}`}>
                  {isConnected ? 'Connected' : 'Disconnected'}
                </span>
              </div>
            )}

            <KimInput
              className="max-h-100 min-h-28 rounded-lg shadow-lg/3"
              placeholder="Ask Kim..."
              value={inputValue}
              onChange={setInputValue}
              onSubmit={handleSendMessage}
              disabled={isSending}
            />
          </div>
        </div>
      </div>

      {/* Side panel - conditionally rendered */}
      <AnimatePresence>
        {sidePanel.isOpen && sidePanel.activeId && (
          <ReferenceSidePanel
            onClose={closeSidePanel}
            references={collectedReferences}
            activeReferenceId={sidePanel.activeId}
            onSelectReference={openSidePanel}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
